import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Icon } from '../../components/ui/Icon'
import { PartThumbnail } from './PartThumbnail'
import type { Quantity } from './domain'

// UID-611 (ADR-0179 D5) — how long a pause in typing must last before the Qty
// input auto-commits without requiring blur/Enter. Mirrors
// `CartLineRow.tsx`'s `QTY_COMMIT_DEBOUNCE_MS` exactly — same UX contract on
// the RFQ side of the same "real quantity input" pattern.
const RFQ_QTY_COMMIT_DEBOUNCE_MS = 500

// UID-611 — shared UI-side upper bound on a single RFQ line's quantity,
// mirroring `CART_MAX_LINE_QUANTITY` (`cart-limits.ts`) at the same value for
// the same reason: fails fast in the browser with friendly copy long before
// the server's own, independent ceiling (`RfqBulkConfigureRequest.quantity`'s
// `maximum: 1000000`) is ever reached. Kept as a local constant rather than a
// cross-vertical import — one number, not worth coupling this file to the
// cart module for.
const RFQ_LINE_QTY_MAX = 100_000

/**
 * UID-327/328 (ADR-0112 D1) — one presentational row for `RfqPartsCard`
 * below. Every optional field is present only when the caller's `isReturned`
 * value warrants it. The component itself does no status branching on
 * `actions`/`unitPriceLabel`/`errorMessage` — it renders them whenever the
 * caller supplies them, and only a quoted (`isReturned`) caller ever does.
 * The select checkbox is the one exception: its column SLOT is always
 * rendered (see `RfqPartsCard`'s own doc comment), and the glyph inside it
 * is explicitly gated on `isReturned` to match the design's per-part
 * `p.canSelect` predicate.
 */
export interface RfqPartsCardRow {
  readonly key: string
  readonly thumbnailRef: string | null | undefined
  readonly name: string
  /** The part-number caption under the name (`partCaption`, `domain.ts`).
   * `undefined` when the part has no part number (UID-606) — omitted
   * entirely rather than a "—" placeholder, so the row below shifts up. */
  readonly caption: string | undefined
  /** UID-267/UID-327 (ADR-0112 D2) — the per-line supporting-file chips,
   * rendered in the "Name & drawing" cell below the caption, hung off the
   * part exactly like the design's `p.hasDrawing` chip (`dc.html:3571`).
   * `null`/`undefined` for a line with no files — no wrapper, no
   * placeholder, mirroring how `files` itself is omitted on the wire for
   * that case. */
  readonly fileChips?: ReactNode
  /** The Requirements column's value for this row — ALWAYS a rendered node,
   * never `null`. Gate 5 W3 correction: this used to say `null` renders "the
   * shared em-dash convention" — that convention is GONE (ADR-0115 D3);
   * `RequirementsCell` (`RfqCard.tsx`) now always renders process (falling
   * back to "To quote") plus an optional `· material` suffix, and
   * `rfq.css`'s `.rfq-requirements-process`/`-material` rules say so too.
   * Passed as a pre-rendered node so both callers (which resolve
   * process/material differently — the owning RFQ's own lines in both
   * cases, but through two separate maps today) share nothing but the
   * markup. */
  readonly requirementsCell: ReactNode
  readonly quantity: Quantity
  /** UID-611 (ADR-0179 D1/D5) — commits a quantity change for THIS line via
   * `PATCH /rfq/{rfqId}/lines/{lineId}`, on EVERY `RfqStatus` (no status
   * gating — the whole point of this ADR). The caller (`RfqCard`/
   * `QuotedLinesPanel`) owns the request itself and replaces its RFQ view
   * state WHOLESALE from the server's response (ADR-0029/ADR-0179 D2); this
   * component never computes or displays a locally-recomputed subtotal.
   *
   * Review-round fix (UID-611) — widened to `Promise<boolean>` (resolves
   * `false` on a failed/errored PATCH) so `RfqLineQtyInput` below can ROLL
   * BACK its own optimistic `draft` display to the last-committed value on
   * failure, instead of leaving the input showing a quantity the server
   * never actually accepted. Both real callers (`RfqCard`/`QuotedLinesPanel`)
   * now resolve this; a caller that still returns `void` simply never gets a
   * rollback (no behavior change from before this fix). */
  readonly onQuantityChange: (next: number) => Promise<boolean> | undefined
  /** True while this row's quantity PATCH is in flight — disables the input
   * so a second edit can't race the first. Defaults to `false`. */
  readonly quantityBusy?: boolean
  /** The Unit price CELL's value — rendered only when present. The column
   * HEADER always renders regardless (ADR-0112 D1: verified at
   * `dc.html:3562`, the header carries no `sc-if` at all — only the cell's
   * inner content is gated on the design's `p.showPrice`). `undefined`/
   * `null` for a non-priced row (a quoted line with no price yet) renders
   * an empty cell, preserving column alignment.
   *
   * UID-586 — widened from `string` to `ReactNode` so the non-quoted path
   * can render the design's `p.isPending` pill here (`dc.html:4224`:
   * spinner + "Quote in progress", `.fx-spin` first, text second) instead
   * of leaving the cell empty while the RFQ is `requested`/`quoting` — a
   * plain formatted price string still satisfies this type unchanged. */
  readonly unitPriceLabel?: ReactNode
  /** The bulk-select checkbox for this row — present only when the caller
   * is rendering a `canSelect` (i.e. `isReturned`) row. The select column
   * SLOT always renders (`dc.html:3562`'s 22px span is unconditional); only
   * the checkbox glyph inside it is gated on `isReturned`, so a non-quoted
   * caller simply never supplies this. */
  readonly checkbox?: {
    readonly checked: boolean
    /** UID-610 — `shiftKey` carries the modifier state off the DOM `click`
     * event (mouse AND the Shift+Space keyboard equivalent — a `click`
     * event carries live modifier state for both, same as
     * `PartsTable.tsx`'s precedent), so the caller can resolve a Gmail/
     * Finder range select via the shared `useRangeSelection` hook. */
    readonly onToggle: (shiftKey: boolean) => void
    readonly ariaLabel: string
  }
  /** The add-to-cart button + "More actions" menu, fully rendered by the
   * caller (`QuotedLinesPanel` owns ALL of that state — per-row add status,
   * the single-flight accept, the row-menu's own modal) — this component
   * never re-implements any of it, only provides the slot. Present only
   * when `isReturned`. Rendered into the trailing action `<td>` (ADR-0115
   * D2 — a DOM move from the name cell; the render-only contract here is
   * unchanged). */
  readonly actions?: ReactNode
  /** The per-row inline failure message (ADR-0064 D1) — present only when
   * this row's add-to-cart just failed. Never carries its own live-region
   * role; the panel-level `aria-live` region (owned by `QuotedLinesPanel`,
   * NOT this component — see that file's doc comment) is the sole
   * announcement path. Rendered into the SAME trailing action `<td>` as
   * `actions` (ADR-0115 D2) — it is the add button's `aria-describedby`
   * target, so it must never sit in a different cell from the control it
   * describes. */
  readonly errorMessage?: ReactNode
  /** UID-586 — the "Needs configuration" filter chip's predicate. Absent
   * (falsy) for every row today: an RFQ's OWN submit gate already refuses a
   * request while any line still needs configuration (`RequestSummaryRail`'s
   * `canSubmit`), so no `RfqPartsCard`-rendered row (pre- or post-quote) is
   * ever actually in that state — this field exists so the design's chip
   * counts honestly, rather than being hidden or hardcoded to zero. */
  readonly needsConfiguration?: boolean
}

export interface RfqPartsCardBulkSelectAll {
  readonly checked: boolean
  readonly indeterminate: boolean
  readonly onToggle: () => void
  readonly inputRef?: React.Ref<HTMLInputElement>
}

export interface RfqPartsCardProps {
  readonly rows: readonly RfqPartsCardRow[]
  /** UID-327/328 (ADR-0112 D1) — the ONE flag every per-affordance gate in
   * this component reads (`dc.html:9791`'s `isReturned=r.status==='returned'`
   * — this build's equivalent is `rfqIsQuoted(rfq.status)`, i.e.
   * `quoted`/`accepted`). Governs: the select-all/per-row checkbox GLYPH
   * (the select column's 22px slot itself is unconditional, matching
   * `dc.html:3562`), and nothing else — the row actions area and the
   * unit-price cell are rendered whenever the caller supplies them, which
   * only an `isReturned` caller ever does. The "Unit price" column HEADER,
   * the select column slot, the thumbnail, name, part number, file chip,
   * process · material, and Qty are all UNGATED (render for every state)
   * per the design's own unconditional header row (`dc.html:3562`).
   */
  readonly isReturned: boolean
  readonly partCount: number
  readonly onAddParts: () => void
  /** Present only when `isReturned` — the header select-all checkbox
   * (`dc.html:9843`'s `showBulkSelect:isReturned`). */
  readonly bulkSelectAll?: RfqPartsCardBulkSelectAll
  /** `aria-label` for the underlying `<table>` — the panel's existing
   * "Quoted lines" label for the quoted path, a new label for the
   * non-quoted path (both callers supply their own; this component invents
   * no default so it stays obviously a required decision at each call
   * site). */
  readonly tableAriaLabel: string
  /** UID-586 — an optional banner rendered between the header bar and the
   * table, exactly where the design puts the Create-RFQ compose flow's
   * in-page submission banner (`RfqSubmissionStatusBanner`). `undefined`
   * for every OTHER caller (the RFQ detail page never passes this — UID-294
   * §4/ADR-0104 D3 already established that a visible status banner here
   * is "not in the design" for THAT surface), so this stays a no-op there. */
  readonly banner?: ReactNode
  /** UID-586 defect fix — the bulk-selection action bar ("N selected" +
   * Move/Delete/Configure), rendered INSIDE this card, after the table, so
   * it shares the card's own border/background instead of floating as a
   * separate box below it. `dc.html:3617-3624`/`:4153-4158` render this
   * bar as a strip nested in the SAME card container as the rows — its own
   * fill (`--bg-1`) is an inset strip, not a bordered/radiused box in its
   * own right (see `rfq.css`'s `.rfq-parts-bulk-bar` comment). `undefined`
   * when nothing is selected. */
  readonly bulkBar?: ReactNode
  /** UID-611 review round (WCAG 2.4.3 Focus Order) — a caller's stable focus
   * anchor: when a row is removed, its own kebab trigger is unmounted WITH
   * it, so the caller moves focus HERE instead (this button never disappears
   * while the card renders at all) rather than letting it fall through to
   * `<body>`. Optional — a caller with no such need (or an older test)
   * simply doesn't pass it. */
  readonly addPartsButtonRef?: React.Ref<HTMLButtonElement>
}

/**
 * UID-327/328 (ADR-0112 D1) — the shared, presentational "Parts in this
 * request" card: the grey header bar (heading + count + "Add parts"),
 * the column-header row, and the per-part rows (`dc.html:3559-3600`).
 * Extracted so BOTH the non-quoted path (`RfqCard`, `isReturned={false}`)
 * and the quoted path (`QuotedLinesPanel`, `isReturned={true}`) render the
 * SAME card instead of two divergent implementations (the dead `<ul>` this
 * replaces, and the panel's own inline `<table>`).
 *
 * Deliberately owns NO state of its own — no fetch, no per-row add status,
 * no accept sequencing, no `aria-live` region. `QuotedLinesPanel` keeps
 * every one of ADR-0064 D1's retry-safety properties (single-flight accept,
 * 409-as-success, reload survival, the ONE panel-level `aria-live` region)
 * entirely unmoved; this component only ever renders the ROWS it is handed.
 * A future caller wanting a fourth "parts of an RFQ" surface mounts this
 * component instead of inventing a fifth rendering of the same table.
 */
export function RfqPartsCard({
  rows,
  isReturned,
  partCount,
  onAddParts,
  bulkSelectAll,
  tableAriaLabel,
  banner,
  bulkBar,
  addPartsButtonRef,
}: RfqPartsCardProps) {
  // UID-586 — the design's "All parts (N)"/"Needs configuration (N)" filter
  // chips (`dc.html:9675`/`:9657`), missing from this shared card entirely
  // until now. Local state: purely presentational, and every existing
  // caller keeps working unchanged (both start on 'all', matching the old
  // unfiltered-always behavior).
  const [partsFilter, setPartsFilter] = useState<'all' | 'config'>('all')
  const needsConfigCount = rows.filter((row) => row.needsConfiguration).length
  const filteredRows =
    partsFilter === 'config' ? rows.filter((row) => row.needsConfiguration) : rows

  return (
    <div className="rfq-parts-card">
      {/* UID-267 (ADR-0092, design L3561) — grey header bar: bold label +
       * "{count} part(s)" (literal design copy, not runtime-pluralized) +
       * the "Add parts" button, pinned to the trailing edge. Deliberately
       * asymmetric with `RfqDetailSummaryRail.tsx`'s "1 part"/"N parts",
       * which DOES pluralize at runtime — both are individually correct for
       * their own surface (this string is design-literal per
       * `dc.html:3561`, the rail's is not), so don't "fix" one to match the
       * other. */}
      {/* `rfq-detail-parts-card-header`, not the bare `rfq-parts-card-header`
       * — that name is `RfqReviewPage.tsx`'s unrelated compose-screen card
       * (see `rfq.css`'s doc comment on this rule for the class-collision
       * this avoids). */}
      <div className="rfq-detail-parts-card-header">
        <h2 className="rfq-card-lines-title">Parts in this request</h2>
        {/* UID-586 — pushes the count + "Add parts" + the new filter chips
         * together to the row's trailing edge (confirmed against a real
         * screenshot of the detail page's own header: the count sits next
         * to the button, not next to the title), reusing the compose
         * page's own spacer rule (`.rfq-parts-card-header-spacer`, a bare
         * `flex: 1` not scoped to any one parent). */}
        <span className="rfq-parts-card-header-spacer" aria-hidden="true" />
        <span className="rfq-card-lines-count">{partCount} part(s)</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          {...(addPartsButtonRef ? { ref: addPartsButtonRef } : {})}
          className="rfq-card-add-parts-btn"
          onClick={onAddParts}
        >
          <Icon name="plus" /> Add parts
        </Button>
        {/* UID-586 — reuses the compose page's OWN filter-chip markup/CSS
         * (`.rfq-parts-filter-chips`/`.rfq-parts-filter-chip`,
         * `PartsReviewTable.tsx`'s equivalent) rather than inventing a
         * second implementation of the same design pattern. Gated on
         * `banner !== undefined` — the SAME signal that scopes the banner
         * itself to the Create-RFQ compose flow's own post-submit render:
         * the design does NOT carry these chips on the RFQ detail
         * page/list (confirmed against a real screenshot of that tab). */}
        {rows.length > 0 && banner !== undefined && (
          <fieldset className="rfq-parts-filter-chips">
            <legend className="sr-only">Filter parts</legend>
            <button
              type="button"
              className="rfq-parts-filter-chip"
              aria-pressed={partsFilter === 'all'}
              onClick={() => setPartsFilter('all')}
            >
              All parts ({rows.length})
            </button>
            <button
              type="button"
              className="rfq-parts-filter-chip"
              aria-pressed={partsFilter === 'config'}
              onClick={() => setPartsFilter('config')}
            >
              Needs configuration ({needsConfigCount})
            </button>
          </fieldset>
        )}
      </div>
      {banner}
      {/* UID-654 (table-responsiveness pass) — mirrored `PartsReviewTable.tsx`'s
       * `.rfq-review-parts-table-scroll` wrapper (UID-565) at the time: this table
       * had no scroll handling, so it could overflow the card at a reduced viewport
       * with no cue. Same wrap-in-a-scrolling-div mechanism, same rule
       * (`.rfq-parts-card-table-scroll`, `rfq.css`). (That precedent wrapper has
       * since been removed from `PartsReviewTable.tsx` per a "no scroll at all"
       * requirement — this card's table is a different component and keeps its
       * own scroll behavior; out of that change's scope.) */}
      <div className="rfq-parts-card-table-scroll">
        <table aria-label={tableAriaLabel} className="rfq-parts-card-table">
          <thead>
            <tr>
              {/* UID-327/328 (ADR-0112 D1) — the select column SLOT renders
               * unconditionally, matching the design's 22px span at
               * `dc.html:3562`, which itself carries an `sc-if` on
               * `r.showBulkSelect` — only the checkbox GLYPH inside the slot
               * is gated on `isReturned`. Keeping the column itself
               * unconditional holds the table's column count (and its
               * `<th scope="col">` associations) invariant across statuses. */}
              <th scope="col" className="rfq-line-select-col">
                <span className="sr-only">Select</span>
                {isReturned && bulkSelectAll && (
                  <input
                    ref={bulkSelectAll.inputRef}
                    type="checkbox"
                    className="checkbox-input rfq-parts-selectall-checkbox"
                    aria-label="Select all parts"
                    checked={bulkSelectAll.checked}
                    aria-checked={bulkSelectAll.indeterminate ? 'mixed' : bulkSelectAll.checked}
                    onChange={bulkSelectAll.onToggle}
                  />
                )}
              </th>
              <th scope="col">Name &amp; drawing</th>
              <th scope="col">Requirements</th>
              <th scope="col" className="rfq-line-qty-col">
                Qty
              </th>
              {/* ADR-0112 D1 — NEVER gated on `isReturned` (verified at
               * `dc.html:3562`: the "Unit price" header span itself carries no
               * `sc-if`, unlike the select-column span on the same line).
               * Only the cell's inner value below is conditional. */}
              <th scope="col" className="rfq-line-price-col">
                Unit price
              </th>
              {/* ADR-0115 D2 — a sixth, trailing, UNCONDITIONAL column for the
               * row's actions, restoring exactly what ADR-0064's UID-289
               * amendment retired. That amendment read `dc.html:3562` (the
               * HEADER row, which does end at Unit price) as "no trailing
               * column" — but the BODY row (`:3583`/`:3584-3596`) places the
               * add button and kebab AFTER the unit-price cell, unlabeled.
               * `sr-only` "Actions" matches the design's own unlabeled column
               * (same ADR-0112 D1 "keep the spacer, drop the content" pattern
               * as the select column's slot above: this `<th>` is
               * unconditional; only the `actions`/`errorMessage` CONTENT below
               * is gated by whether the caller supplies it, which only an
               * `isReturned` caller ever does). */}
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.key}>
                <td className="rfq-line-select-col">
                  {isReturned && row.checkbox && (
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      aria-label={row.checkbox.ariaLabel}
                      checked={row.checkbox.checked}
                      // UID-610 — reads `event.shiftKey` off `onClick`;
                      // `onChange` carries no modifier-key state, so it
                      // stays a no-op (see `checkbox.onToggle`'s doc
                      // comment above).
                      onClick={(event) => row.checkbox?.onToggle(event.shiftKey)}
                      onChange={() => {}}
                    />
                  )}
                </td>
                <td>
                  <div className="rfq-line-name-cell">
                    <PartThumbnail className="rfq-line-thumb" url={row.thumbnailRef} />
                    <div className="rfq-line-name-block">
                      <span className="rfq-line-name" title={row.name}>
                        {row.name}
                      </span>
                      {/* UID-606 — omit the row when there's no part number,
                       * so no "—" placeholder and the file chip shifts up. */}
                      {row.caption ? (
                        <span className="caption-text rfq-line-partnum">{row.caption}</span>
                      ) : null}
                      {row.fileChips}
                    </div>
                  </div>
                </td>
                <td>{row.requirementsCell}</td>
                <td className="rfq-line-qty-col">
                  <RfqLineQtyInput
                    quantity={row.quantity}
                    onCommit={row.onQuantityChange}
                    {...(row.quantityBusy !== undefined ? { busy: row.quantityBusy } : {})}
                    ariaLabel={`Quantity for ${row.name}`}
                  />
                </td>
                <td className="rfq-line-price-col">
                  {row.unitPriceLabel ? row.unitPriceLabel : null}
                </td>
                {/* ADR-0115 D2 — the trailing action cell. `.rfq-line-actions`
                 * moves here VERBATIM (same wrapper, same children, same
                 * classes) from the name cell above; the row's inline error
                 * `<p>` moves WITH it (it is the add button's
                 * `aria-describedby` target — leaving a control's description
                 * in a different `<td>` from the control is a defect). Empty,
                 * unconditional slot for a non-returned row, matching Unit
                 * price's own "spacer, not omitted" cell above. */}
                <td className="rfq-line-actions-col">
                  {row.actions && <div className="rfq-line-actions">{row.actions}</div>}
                  {row.errorMessage}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {bulkBar}
    </div>
  )
}

/**
 * UID-611 (ADR-0179 D1/D5) — the real, persisted quantity `<input>` for one
 * RFQ line, rendered on EVERY row regardless of `RfqStatus` (no exceptions —
 * the whole point of this ADR). Wired the exact same way
 * `CartLineRow.tsx`'s own quantity input is: a local draft value, commit on
 * blur/Enter, and an auto-commit on a pause in typing
 * (`RFQ_QTY_COMMIT_DEBOUNCE_MS`) so recalculation never fires on a
 * half-typed value. `onCommit` is the caller's PATCH call; this component
 * never talks to the network itself and never recomputes a subtotal/total —
 * the caller replaces its RFQ view state wholesale from the server's
 * response (ADR-0029/ADR-0179 D2).
 *
 * Retires `RfqDetailSummaryRail.tsx`'s narrow, client-only, single-part
 * quantity input (ADR-0179 D5) — this is the one real input, everywhere.
 */
function RfqLineQtyInput({
  quantity,
  onCommit,
  busy = false,
  ariaLabel,
}: {
  readonly quantity: Quantity
  readonly onCommit: (next: number) => Promise<boolean> | undefined
  readonly busy?: boolean
  readonly ariaLabel: string
}) {
  const errorId = useId()
  const [draft, setDraft] = useState(String(quantity.value))
  const [invalid, setInvalid] = useState(false)
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    setDraft(String(quantity.value))
  }, [quantity.value])

  function commit(next: number) {
    clearTimeout(commitTimerRef.current)
    setInvalid(false)
    const clamped = Math.min(RFQ_LINE_QTY_MAX, Math.max(1, Math.round(next)))
    // The value in effect just before this optimistic edit — what a FAILED
    // commit below rolls the draft back to.
    const previous = quantity.value
    setDraft(String(clamped))
    if (clamped !== previous) {
      const result = onCommit(clamped)
      // Review-round fix (rollback-on-failure) — `onCommit` now optionally
      // resolves `false` on a failed PATCH; revert the OPTIMISTIC `draft` back
      // to the last-known-good value rather than leaving the input showing a
      // quantity the server never actually persisted. Guarded so a caller
      // that still returns `void` (none today) keeps working unchanged.
      if (result && typeof result.then === 'function') {
        void result.then((ok) => {
          if (ok === false) setDraft(String(previous))
        })
      }
    }
  }

  function commitDraft() {
    const parsed = Number(draft)
    // Gate 5 review round (UID-611) — a non-numeric/malformed draft (e.g.
    // "abc") used to fall through to `commit(NaN)`: `Math.round`/`Math.max`/
    // `Math.min` all propagate `NaN`, and `NaN !== quantity.value` is always
    // `true` (NaN is never `===` anything, including itself), so `onCommit`
    // fired with an invalid quantity — a PATCH the API's own `makeQuantity`
    // factory would 422 on, but only AFTER a wasted round trip and with no
    // client-side signal. Treated exactly like the empty-string case: revert
    // + inline error, no network call.
    if (draft.trim() === '' || !Number.isFinite(parsed)) {
      clearTimeout(commitTimerRef.current)
      setDraft(String(quantity.value))
      setInvalid(true)
      return
    }
    commit(parsed)
  }

  // Auto-commit on a pause in typing — same discipline as CartLineRow's own
  // effect (see that file's comment for the full rationale): keyed ONLY on
  // `draft` so it doesn't restart on every render, reading `commit`/
  // `quantity.value` from the current closure at fire time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    const draftValue = Number(draft)
    const isUncommittable =
      draft.trim() === '' || !Number.isFinite(draftValue) || draftValue === quantity.value
    if (isUncommittable) {
      return
    }
    const timer = setTimeout(() => commit(draftValue), RFQ_QTY_COMMIT_DEBOUNCE_MS)
    commitTimerRef.current = timer
    return () => clearTimeout(timer)
  }, [draft])

  return (
    <span className="rfq-line-qty-edit">
      <input
        inputMode="numeric"
        min={1}
        max={RFQ_LINE_QTY_MAX}
        className="rfq-line-qty-input"
        aria-label={ariaLabel}
        aria-invalid={invalid}
        aria-describedby={invalid ? errorId : undefined}
        disabled={busy}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commitDraft()
          }
        }}
      />
      {invalid && (
        <p id={errorId} role="alert" className="form-error-text rfq-line-qty-error">
          Enter a quantity — reverted to {quantity.value}.
        </p>
      )}
    </span>
  )
}
